import Image from "@/components/Atoms/Image";
import CustomLink from "@/components/Atoms/CustomLink";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { routerHandler } from "@/lib/routes/router-links-handler";
import {
  getPortfolioSlug,
  resolveButtonCustomIcon,
  resolveButtonIconColor,
  resolvePageRelatedCta,
} from "@/lib/functions";
import { useTranslation } from "next-i18next";

interface RecipePairingProps {
  pairing: any;
  config: any;
  portfolioSlug?: string;
  locale?: string;
  layoutType?: string;
}

function getRecipePairingLink({
  pairing,
  config,
  portfolioSlug,
  locale,
}: RecipePairingProps) {
  const ctaConfig = resolvePageRelatedCta(config?.ctaConfig);
  const pairingImage = pairing?.factSheetImage;
  const portfolioSlugResolved =
    portfolioSlug ??
    getPortfolioSlug(ctaConfig?.portfolioConfiguration, locale);

  const productLink =
    pairing && portfolioSlugResolved
      ? routerHandler({
          typename: pairing.__typename,
          slug: [
            portfolioSlugResolved,
            pairing?.productTypeSpecific?.slug,
            pairing?.slug,
          ],
          locale,
        })
      : null;

  return { ctaConfig, pairingImage, productLink, portfolioSlugResolved };
}

export default function RecipePairingColumn({
  pairing,
  config,
  portfolioSlug,
  locale,
}: RecipePairingProps) {
  const { t } = useTranslation();
  const { ctaConfig, pairingImage, productLink } = getRecipePairingLink({
    pairing,
    config,
    portfolioSlug,
    locale,
  });

  if (!pairing) return null;

  return (
    <aside
      className="hidden w-full max-w-[30rem] flex-col items-center gap-6 lg:flex lg:sticky lg:top-8 lg:self-start"
    >
      {pairingImage && (
        <Image className="w-full max-w-[30rem]" data={pairingImage} />
      )}
      {productLink && ctaConfig && (
        <ButtonMaster
          anim="slideLeft"
          type={ctaConfig.buttonStyle}
          cta={ctaConfig.typeColor?.color}
          color={ctaConfig.labelColor?.color}
          customIcon={resolveButtonCustomIcon(ctaConfig)}
          customColor={resolveButtonIconColor(ctaConfig)}
          link={productLink}
        >
          {t("viewWineDetail")}
        </ButtonMaster>
      )}
    </aside>
  );
}

export function RecipePairingMobileCta({
  pairing,
  config,
  portfolioSlug,
  locale,
  layoutType,
}: RecipePairingProps) {
  const { t } = useTranslation();
  const { ctaConfig, productLink } = getRecipePairingLink({
    pairing,
    config,
    portfolioSlug,
    locale,
  });

  const wineName = pairing?.productName;
  if (!pairing || !productLink || !wineName) return null;

  const label =
    layoutType === "cocktail"
      ? `${t("cocktailMadeWith")} ${wineName}`
      : `${t("recipeMadeWith")} ${wineName}`;

  const accentColor =
    ctaConfig?.typeColor?.color?.hex ??
    ctaConfig?.labelColor?.color?.hex ??
    config?.headlineTextColor?.color?.hex;

  return (
    <div className="flex justify-center pb-8">
      <CustomLink
        href={productLink}
        className="border-b py-1 text-base font-medium transition-transform duration-500 hover:scale-110"
        style={{
          color: accentColor,
          borderColor: accentColor,
        }}
      >
        {label}
      </CustomLink>
    </div>
  );
}
